import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useLocation } from '@/services/router/compat';
import { createRealtimeBridge } from './bridge';
import { realtimeBridgeStore } from './bridge-store';
import { createCentrifugeConnection } from './centrifuge-connection';
import type { ActiveRealtime } from './events';
import { isDesignStudioPath } from './design-studio-route';
import type { Peer } from './peers';
import type { PeerStore } from './peer-store';
import { getTabId, onTabIdChange } from './tab-id';
import { fetchRealtimeToken } from './token-api';
import { useRealtimeInvalidation } from './use-realtime-invalidation';
import { isEmbeddedWorkflowBuilderPath } from './embedded-workflow-builder-route';
import { createWorkspacePresence, type WorkspacePresence } from './workspace-presence';

/** Puts the bridge in `realtimeBridgeStore` while `realtime` is set and clears it when it clears. */
function useRealtimeBridge(realtime: ActiveRealtime | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (realtime === null) return;
    const bridge = createRealtimeBridge({ ...realtime, queryClient });
    realtimeBridgeStore.set(bridge);
    return () => {
      if (realtimeBridgeStore.get() === bridge) realtimeBridgeStore.set(null);
    };
  }, [realtime, queryClient]);
}

const NO_PEERS: Peer[] = [];
const noopSubscribe = () => () => {};
const getNoPeers = () => NO_PEERS;

/** The workspace peer store while realtime is connected; null otherwise. */
const WorkspacePeersContext = createContext<PeerStore | null>(null);

/**
 * Every other tab in the workspace with its last `location`, or an empty list while realtime is
 * not connected (not on a Design Studio page).
 */
export function useWorkspacePeers(): Peer[] {
  const store = useContext(WorkspacePeersContext);
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getList ?? getNoPeers);
}

/** Joins the workspace channel while connected and reports this tab's route on it. */
function useWorkspacePresence(realtime: ActiveRealtime | null, pathname: string): PeerStore | null {
  const [presence, setPresence] = useState<WorkspacePresence | null>(null);

  useEffect(() => {
    if (realtime === null) return;
    const joined = createWorkspacePresence(realtime.transport, realtime.environmentId);
    setPresence(joined);
    return () => {
      joined.close();
      setPresence(null);
    };
  }, [realtime]);

  useEffect(() => {
    presence?.setLocation(pathname);
  }, [presence, pathname]);

  return presence?.store ?? null;
}

type RealtimeProviderProps = { children: ReactNode };

/**
 * Opens one Centrifuge connection for the tab, for the current workspace, while the route is a
 * Design Studio page or the embedded workflow builder. Moving between those pages keeps the
 * connection; leaving them closes it. The workflow builder only needs entity changes, so the
 * bridge and presence stay on Design Studio pages. The children render the same either way, so a
 * route change never remounts the app below.
 */
export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { pathname } = useLocation();
  const workspace = useCurrentWorkspace();
  const onDesignStudio = isDesignStudioPath(pathname);
  const wantsRealtime = onDesignStudio || isEmbeddedWorkflowBuilderPath(pathname);
  const environmentId = wantsRealtime && workspace?.id != null ? workspace.id : null;
  const tab = useSyncExternalStore(onTabIdChange, getTabId);

  // Creating a connection is free; it only dials once started.
  const connection = useMemo(
    () => (environmentId === null ? null : createCentrifugeConnection(() => fetchRealtimeToken(environmentId), tab)),
    [environmentId, tab],
  );
  useEffect(() => {
    connection?.start();
    return () => connection?.stop();
  }, [connection]);

  const realtime = useMemo(
    () => (connection === null || environmentId === null ? null : { transport: connection, environmentId, tab }),
    [connection, environmentId, tab],
  );

  const designStudioRealtime = onDesignStudio ? realtime : null;
  useRealtimeBridge(designStudioRealtime);
  useRealtimeInvalidation(realtime);
  const workspacePeers = useWorkspacePresence(designStudioRealtime, pathname);

  return <WorkspacePeersContext value={workspacePeers}>{children}</WorkspacePeersContext>;
}
